/**
 * CRM-LIST-VIEWS-V1 (V1-P7; rider R-16) -- THE CANONICAL LEADS LIST
 * (`/dashboard/leads`), upgraded IN PLACE from the LEADS-WORKSPACE page
 * (C-4: one canonical list -- the V5-P10 fold made this route the ONE list;
 * the owner route stays a redirect shim; no second list is created).
 *
 * SERVER-SIDE EVERYTHING (E-3): filters, sort, and counts run on
 * GET /api/leads/workspace with the validated list contract -- fences
 * before data, never client-side filtering of over-fetched rows. Every
 * control here only writes URL state; the SERVER shapes both data and
 * filter options (C-6): tier projection strips unlawful fields, the
 * assignee directory follows the assign-authority gate, and source
 * filtering exists only for tiers that may see source (FQ-1).
 *
 * URL = the canonical view state (C-9): view / filters / sort / columns
 * ride the querystring (lead-list-url.ts codec -- versioned, allowlisted,
 * length-bounded, PHI-free); copy/bookmark reproduces the exact view.
 * PERSISTENCE RUNG (C-2/C-3): URL-only. No database saved views (no proven
 * preference principal -- the USER-PREFERENCE-STORE-V1 seam owns that
 * future); no browser-local named views (shared clinic workstations leak).
 *
 * SEARCH stays a CLIENT-side narrow over the fetched page, EPHEMERAL
 * (never in the URL -- names typed into search must not reach bookmarks /
 * history / logs; the C-9 PHI ruling) and DISCLOSED on-page.
 *
 * DRAWER (C-12 / the 360 law): each row's Quick view opens the EXISTING
 * LeadQuickView drawer IN PLACE (component state, no navigation) -- the
 * full Lead-360 page stays the primary row link (the E7 founder ruling:
 * directory rows open the full page; the drawer is the in-place act).
 * Closing the drawer preserves filters/sort/columns/scroll by construction
 * (the URL and the fetched page are untouched).
 *
 * NO bulk-selection checkboxes: bulk actions are out of scope, so bulk
 * selection is omitted entirely (C-13).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  fetchLeadDispositionOptions,
  fetchLeadsList,
  saveLeadDisposition,
  type LeadDispositionOptionsResponse,
  type LeadsListResponse,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { portalErrorStatus, safeErrorMessage } from '@/lib/portal-api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { clinicLabelOf, useClinicLens } from '@/dashboard/clinic-context';
import ClinicScopeLabel from '@/dashboard/ui/ClinicScopeLabel';
import {
  buildLead360View,
  type Lead360Input,
} from '@/dashboard/owner/lead-360-view';
import LeadQuickView from '@/dashboard/leads/LeadQuickView';
import LeadEnquirySearchModal from '@/dashboard/leads/LeadEnquirySearchModal';
import LeadDispositionPanel from '@/dashboard/leads/LeadDispositionPanel';
import LeadViewSelector from '@/dashboard/leads/LeadViewSelector';
import LeadFilterPanel from '@/dashboard/leads/LeadFilterPanel';
import { buildQuickViewFromLead360 } from '@/dashboard/leads/lead-quickview-view';
import {
  orderRecordsByRecency,
  readRecentlyViewedIds,
  recordLeadOpened,
} from '@/dashboard/leads/lead-recently-viewed';
import {
  compactSourceSummary,
  formatTimestampIst,
  LINK_STATUS_LABEL,
} from '@/dashboard/owner/Lead360Panel';
import {
  formatLeadRef,
  latestActivityInstant,
  latestActivitySummaryShort,
} from '@/dashboard/owner/leads-object-view';
import {
  ALL_CLINICS_VALUE,
  buildNeedsAttention,
  compactActivityDate,
  leadMatchesQuery,
  relativeUpdatedLabel,
  workspaceLeadDetailHref,
  workspaceRoleLabel,
  type NeedsAttentionItem,
} from '@/dashboard/leads/leads-workspace-view';
import {
  activeFilterChips,
  defaultColumnsForRole,
  defaultViewForRole,
  LEAD_LIST_AGE_PRESETS,
  LEAD_LIST_SLA_CLASSES,
  LEAD_LIST_SORTS,
  LEAD_LIST_SOURCES,
  LEAD_LIST_STAGES,
  canActOnLeadsWorkspace,
  lawfulColumnsForRole,
  isLeadListRole,
  removeFilterChip,
  resolveActiveViewId,
  viewsForRole,
  type LeadListFilterState,
  type LeadListRole,
  type LeadListSortKey,
  type LeadListViewState,
} from '@/dashboard/leads/lead-list-contract';
import {
  apiParamsForState,
  parseLeadListUrl,
  serializeLeadListUrl,
} from '@/dashboard/leads/lead-list-url';
import {
  AcBadge,
  ColumnChooser,
  EmptyState,
  SortControl,
  type FilterGroupDef,
} from '@/dashboard/ui';
import {
  classMeta,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';

/** Narrow a wire breach-class token to the known five (an unknown token
 *  renders its raw token honestly, never a guessed title). */
function knownBreachClass(token: string): SlaBreachClass | null {
  return (SLA_CLASS_ORDER as readonly string[]).includes(token)
    ? (token as SlaBreachClass)
    : null;
}

/** Page size -- same convention as before (backend cap is 200). */
export const WORKSPACE_LEADS_LIMIT = 100;

const WORKSPACE_EMPTY_COPY =
  'No leads in this scope yet. Capture and source-attribution data may still be pending.';

/**
 * M6 (AEDS): the server-filtering explainer, demoted from a prose banner to
 * an accessible (i) icon-hint. This exact text is the icon-hint button's
 * accessible NAME (screen-reader reachable) and its hover/focus popover, so
 * the essential instruction is never hidden on hover alone.
 */
export const WORKSPACE_SCOPE_HINT =
  'Records are scoped to your verified clinic access. Filters and sorting run ' +
  'on the server. Phone numbers are never listed here -- open a lead for the ' +
  'audited reveal.';

// --- Payload shape guard (V7-P4-SLICE-2 finding fix c; STITCH-SAFETY-NET-V1
// finding 2) -----------------------------------------------------------------
//
// A 200 payload MISSING the workspace contract (list metadata / records /
// role / clinics -- e.g. a degraded read or a generic mock) previously threw
// on `data.list.assigneeOptions` inside the body and crashed the whole page.
// The shape is guarded ONCE here, fail-closed to an honest EmptyState; the
// body below stays contract-typed. Thrown fetches keep their own
// DashboardLoadError path -- this guard covers the 200-but-malformed case.

export const WORKSPACE_PAYLOAD_UNAVAILABLE_TITLE =
  'The leads workspace could not load its data.';
export const WORKSPACE_PAYLOAD_UNAVAILABLE_BODY =
  'The server response was missing the list scope metadata. Retry; if this repeats, the leads read is degraded.';

/** True when the wire payload carries the full workspace contract (every
 *  field the body dereferences without its own guard). Pure; test-pinned. */
export function leadsPayloadRenderable(data: LeadsListResponse | null): boolean {
  if (data === null || typeof data !== 'object') return false;
  const shape = data as Partial<LeadsListResponse>;
  return (
    shape.list !== undefined &&
    shape.list !== null &&
    Array.isArray(shape.list.assigneeOptions) &&
    Array.isArray(shape.list.ignoredFilters) &&
    Array.isArray(shape.records) &&
    typeof shape.role === 'string' &&
    Array.isArray(shape.clinics)
  );
}

/** The honest degraded state (pure; rendered instead of a crash). */
export function LeadsWorkspaceUnavailable({
  onRetry,
}: {
  onRetry?: () => void;
}): JSX.Element {
  return (
    <EmptyState
      title={WORKSPACE_PAYLOAD_UNAVAILABLE_TITLE}
      body={WORKSPACE_PAYLOAD_UNAVAILABLE_BODY}
      action={
        onRetry !== undefined ? (
          <button
            type="button"
            className="lead-list__retry"
            data-testid="lead-list-retry"
            onClick={onRetry}
          >
            Retry
          </button>
        ) : undefined
      }
      testId="leads-payload-unavailable"
    />
  );
}

// ---- The registry-driven table ---------------------------------------------

/** Compact assigned-to cell text from the ownership overlay (never the
 *  OWNER role word -- rider R-53). */
function assignedCellText(record: Lead360Input): string {
  const oa = record.ownerAssignment;
  if (oa === undefined || oa === null) return '-';
  if (oa.assigned === false) return 'Unassigned';
  return oa.ownerLabel ?? 'Assigned';
}

/**
 * ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1: the stage chip carries the
 * ratified stage-color grammar (design-language 02 section 4) instead of a
 * bare uppercase chip -- one hue per canon stage, the SAME meaning logic
 * everywhere a stage renders. Presentation mapping only (labels verbatim
 * from LEAD_LIST_STAGES; unknown stage falls back to the neutral chip).
 */
const STAGE_CELL_CHIP_CLASS: Readonly<Record<string, string>> = {
  new: 'status-chip status-chip--stage-new',
  contacted: 'status-chip status-chip--stage-contacted',
  qualified: 'status-chip status-chip--stage-qualified',
  appointment_booked: 'status-chip status-chip--stage-appointment_booked',
  arrived: 'status-chip status-chip--stage-arrived',
  patient: 'status-chip status-chip--stage-patient',
  converted_to_revenue: 'status-chip status-chip--stage-converted_to_revenue',
  lost: 'status-chip status-chip--stage-lost',
};

function StageCell({ record }: { record: Lead360Input }): JSX.Element {
  const stage = record.stage ?? null;
  if (stage === null) {
    return <span className="lead-list__muted">Not recorded</span>;
  }
  const def = LEAD_LIST_STAGES.find((s) => s.token === stage);
  return (
    <span className={STAGE_CELL_CHIP_CLASS[stage] ?? 'status-chip status-chip--neutral'}>
      {def?.label ?? stage}
    </span>
  );
}

function SlaCell({ record }: { record: Lead360Input }): JSX.Element {
  const state = record.slaState;
  if (state === undefined) {
    return (
      <span className="lead-list__muted" title="SLA state not available">
        -
      </span>
    );
  }
  if (state.breaches.length === 0) {
    return <span className="lead-list__sla-clear">Clear</span>;
  }
  return (
    <span className="lead-list__sla-chips">
      {state.breaches.map((b) => {
        const known = knownBreachClass(b.breachClass);
        const title = known !== null ? classMeta(known).title : b.breachClass;
        return (
          <AcBadge
            key={b.breachClass}
            tone={b.severity === 'red' ? 'danger' : 'pending'}
            title={`${title} - waiting ${b.ageMinutes} min`}
          >
            {title}
          </AcBadge>
        );
      })}
    </span>
  );
}

export interface LeadListTableProps {
  readonly records: readonly Lead360Input[];
  /** Visible column ids (already role-normalized; registry order). */
  readonly columns: readonly string[];
  readonly role: LeadListRole;
  readonly clinicValue: string;
  readonly sort: LeadListSortKey;
  readonly onSortKey?: (key: LeadListSortKey) => void;
  readonly onQuickView?: (record: Lead360Input) => void;
  /** Fires when a row's full-record door (ref / name link) is opened -- the
   *  list records it into the "Recently viewed" client memory. */
  readonly onOpenRecord?: (record: Lead360Input) => void;
  /** The open quick-view row (clear selected state -- Mission 9). */
  readonly selectedId?: string | null;
}

/**
 * The registry-driven leads table (C-10): headers and cells render ONLY the
 * caller's normalized lawful column set; sortable headers toggle the
 * server-validated sort keys with a deterministic indicator; sticky header;
 * horizontal scroll on narrow screens (the table never widens the page).
 */
export function LeadListTable({
  records,
  columns,
  role,
  clinicValue,
  sort,
  onSortKey,
  onQuickView,
  onOpenRecord,
  selectedId = null,
}: LeadListTableProps): JSX.Element {
  const defs = lawfulColumnsForRole(role).filter((c) => columns.includes(c.id));
  const clinicForHref = clinicValue === ALL_CLINICS_VALUE ? null : clinicValue;
  return (
    <div className="lead-list__table-wrap" data-testid="lead-list-table">
      <table className="lead-list__table lead-list__table--dense">
        <thead>
          <tr>
            {defs.map((c) => {
              if (c.sort === undefined || onSortKey === undefined) {
                return <th key={c.id}>{c.label}</th>;
              }
              const active = sort === c.sort.asc || sort === c.sort.desc;
              const nextKey = sort === c.sort.desc ? c.sort.asc : c.sort.desc;
              const ariaSort = !active
                ? 'none'
                : sort === c.sort.asc
                  ? 'ascending'
                  : 'descending';
              return (
                <th key={c.id} aria-sort={ariaSort}>
                  <button
                    type="button"
                    className={
                      active
                        ? 'lead-list__sort-header lead-list__sort-header--active'
                        : 'lead-list__sort-header'
                    }
                    data-testid={`lead-list-sort-${c.id}`}
                    onClick={() => onSortKey(nextKey)}
                  >
                    {c.label}
                    {active ? (
                      <span className="lead-list__sort-caret" aria-hidden="true">
                        {sort === c.sort.asc ? ' \u25B2' : ' \u25BC'}
                      </span>
                    ) : null}
                  </button>
                </th>
              );
            })}
            {onQuickView !== undefined ? <th className="lead-list__th-actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const view = buildLead360View(record);
            const selected = selectedId !== null && record.safeLeadId === selectedId;
            return (
              <tr
                key={record.safeLeadId}
                data-testid={`lead-list-row-${record.safeLeadId}`}
                data-selected={selected ? 'yes' : undefined}
                className={selected ? 'lead-list__row--selected' : undefined}
              >
                {defs.map((c) => {
                  switch (c.id) {
                    case 'lead':
                      return (
                        <td key={c.id} className="lead-list__ref lead-list__num">
                          <Link
                            to={workspaceLeadDetailHref(record.safeLeadId, clinicForHref)}
                            onClick={() => onOpenRecord?.(record)}
                          >
                            {formatLeadRef(record.safeLeadId)}
                          </Link>
                        </td>
                      );
                    case 'name':
                      // The record NAME is the primary row door (the CRM
                      // list grammar) -- same lawful destination the ref
                      // link already held; no new navigation.
                      return (
                        <td key={c.id} className="lead-list__name">
                          {record.leadName !== null && record.leadName !== undefined ? (
                            <Link
                              to={workspaceLeadDetailHref(record.safeLeadId, clinicForHref)}
                              onClick={() => onOpenRecord?.(record)}
                            >
                              {record.leadName}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                      );
                    case 'stage':
                      return (
                        <td key={c.id}>
                          <StageCell record={record} />
                        </td>
                      );
                    case 'sla':
                      return (
                        <td key={c.id}>
                          <SlaCell record={record} />
                        </td>
                      );
                    case 'clinic':
                      return (
                        <td key={c.id}>
                          {record.clinicLabel ??
                            (record.clinicId !== null ? `Clinic ${record.clinicId}` : '-')}
                        </td>
                      );
                    case 'source':
                      return <td key={c.id}>{compactSourceSummary(record)}</td>;
                    case 'assigned':
                      return <td key={c.id}>{assignedCellText(record)}</td>;
                    case 'latest_activity': {
                      // M5 (AEDS): terse activity -- an outcome CHIP + a
                      // compact date, never a sentence in a cell. A pure
                      // presentation transform of the wire activity: the chip
                      // is the honest short summary (neutral tone -- no
                      // invented semantic meaning), the date is the latest
                      // event instant (absent when the stream has no events).
                      const label = latestActivitySummaryShort(view);
                      const instant = latestActivityInstant(view);
                      const date =
                        instant !== null
                          ? compactActivityDate(formatTimestampIst(instant))
                          : null;
                      return (
                        <td key={c.id} className="lead-list__activity">
                          <span className="lead-list__activity-cell">
                            <AcBadge
                              tone="neutral"
                              testId={`lead-list-activity-${record.safeLeadId}`}
                            >
                              <span
                                className="lead-list__activity-chip"
                                title={label}
                              >
                                {label}
                              </span>
                            </AcBadge>
                            {date !== null ? (
                              <span className="lead-list__activity-date">{date}</span>
                            ) : null}
                          </span>
                        </td>
                      );
                    }
                    case 'telephony':
                      return <td key={c.id}>{LINK_STATUS_LABEL[record.callLinked]}</td>;
                    case 'walkin':
                      return <td key={c.id}>{LINK_STATUS_LABEL[record.walkinLinked]}</td>;
                    case 'created':
                      return (
                        <td key={c.id} className="lead-list__num">
                          {record.createdAt !== null && record.createdAt !== undefined
                            ? formatTimestampIst(record.createdAt)
                            : '-'}
                        </td>
                      );
                    default:
                      return <td key={c.id}>-</td>;
                  }
                })}
                {onQuickView !== undefined ? (
                  <td className="lead-list__actions">
                    <button
                      type="button"
                      className="lead-list__quickview ac-btn ac-btn--ghost ac-btn--sm"
                      data-testid={`lead-list-quickview-${record.safeLeadId}`}
                      onClick={() => onQuickView(record)}
                    >
                      Quick view
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---- Result truth (C-8 honesty line) ----------------------------------------

export function resultTruthLine(list: LeadsListResponse['list']): string {
  if (list.slaStateUnavailable === true) {
    return 'SLA state is unavailable right now, so the SLA filter cannot run. Retry shortly.';
  }
  if (list.assigneeUnresolved === true) {
    return 'Your login has no staff identity yet, so "Assigned to: Me" matches nothing.';
  }
  if (list.filteredTotal === null) {
    return `Showing ${list.visibleRows} leads. The matching total is unavailable right now.`;
  }
  if (list.filteredTotalIsPartial && list.scanLimit !== null) {
    return `Showing ${list.visibleRows} matches within the newest ${list.scanLimit} leads scanned. Narrow the filters for a complete view.`;
  }
  if (list.scanCapped) {
    return `Showing the first ${list.visibleRows} of ${list.filteredTotal} matching leads.`;
  }
  if (list.filteredTotal > list.visibleRows) {
    return `${list.filteredTotal} matching leads. Showing ${list.visibleRows} on this page.`;
  }
  return `${list.filteredTotal} matching lead${list.filteredTotal === 1 ? '' : 's'}.`;
}

// ---- The Salesforce count line ("N items - sorted by X") -------------------

/** Human sort label for the count line (recency view sorts client-side). */
export function countSortLabel(sort: LeadListSortKey, recency: boolean): string {
  if (recency) return 'Recently opened';
  return LEAD_LIST_SORTS.find((s) => s.key === sort)?.label ?? 'Created';
}

/**
 * The Salesforce list-grammar headline over the ON-SCREEN rows (Mission 3):
 * "N items - sorted by X". `count` is the count of rows actually rendered
 * (after the client search / recency narrowing), so the five-second test
 * reads the true on-screen number; the server-cap honesty stays on the
 * separate result-truth line below it. Pure; test-pinned.
 */
export function countSummaryLine(
  count: number,
  sort: LeadListSortKey,
  recency: boolean,
): string {
  return `${count} item${count === 1 ? '' : 's'} - sorted by ${countSortLabel(sort, recency)}`;
}

// ---- The presentational body (renderable without fetch, for tests) ---------

export interface LeadsListBodyProps {
  readonly data: LeadsListResponse;
  readonly role: LeadListRole;
  readonly state: LeadListViewState;
  readonly clinicValue: string;
  readonly onState?: (next: LeadListViewState) => void;
  readonly query: string;
  readonly onQueryChange?: (q: string) => void;
  readonly onQuickView?: (record: Lead360Input) => void;
  /** Record that the caller opened a lead's full record (the row door) -- so
   *  the "Recently viewed" client memory reflects it (LEADS-LIST-SALESFORCE-
   *  VIEWS-V1). List-surface only; never a server read. */
  readonly onOpenRecord?: (record: Lead360Input) => void;
  readonly onCopyUrl?: () => void;
  readonly copied?: boolean;
  /** R-CD-B1: opens the search-first "Find by phone" modal (R-CD-R1 --
   *  creation only as the NOT-FOUND outcome of search; the button opens
   *  SEARCH, never a standalone add form). Absent = no door renders. */
  readonly onFindByPhone?: () => void;
  /** The open quick-view row id (selected-row state -- Mission 9). */
  readonly selectedId?: string | null;
  /** The Salesforce right-side filter panel open state (Mission 2). */
  readonly filtersOpen?: boolean;
  readonly onToggleFilters?: (open: boolean) => void;
  /** The client "Recently viewed" recency order (most-recent-first lawful ids
   *  the caller opened). Only ever intersected with the fenced page. */
  readonly recentIds?: readonly string[];
  /** M3 freshness label ("updated 2 min ago"), derived from the last
   *  SUCCESSFUL fetch instant; omitted (null/undefined) renders no freshness. */
  readonly freshnessLabel?: string | null;
  /** M3 refresh affordance -- re-runs the EXISTING read preserving view /
   *  filter / search state. Absent (tests / no page) renders no Refresh. */
  readonly onRefresh?: () => void;
  /** True while a manual refresh is in flight (disables Refresh, shows a
   *  spinner) -- prevents duplicate requests, never falsely reports success. */
  readonly refreshing?: boolean;
}

export function LeadsListBody({
  data,
  role,
  state,
  clinicValue,
  onState,
  query,
  onQueryChange,
  onQuickView,
  onOpenRecord,
  onCopyUrl,
  copied = false,
  onFindByPhone,
  selectedId = null,
  filtersOpen = false,
  onToggleFilters,
  recentIds = [],
  freshnessLabel = null,
  onRefresh,
  refreshing = false,
}: LeadsListBodyProps): JSX.Element {
  const views = viewsForRole(role);
  // The active view id is client-recency aware: "Recently viewed" collides
  // with the base read on filters, so its identity rides the explicit view id
  // (resolveActiveViewId), never a filter match.
  const activeViewId = resolveActiveViewId(role, state);
  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const recencyActive = activeView?.client === 'recency';
  const columns = state.columns ?? defaultColumnsForRole(role);
  const chips = activeFilterChips(state.filters);
  const lawfulCols = lawfulColumnsForRole(role);

  const setFilters = (filters: LeadListFilterState): void => {
    onState?.({ ...state, filters, view: null });
  };

  const filterGroups: FilterGroupDef[] = [
    {
      kind: 'tokens',
      key: 'stage',
      label: 'Stage',
      options: LEAD_LIST_STAGES.map((s) => ({ token: s.token, label: s.label })),
      selected: state.filters.stages,
    },
    {
      kind: 'tokens',
      key: 'sla',
      label: 'SLA state',
      options: LEAD_LIST_SLA_CLASSES.map((c) => ({ token: c.token, label: c.label })),
      selected: state.filters.slaClasses,
    },
    // CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: the "Assigned to"
    // group (incl. the "Anyone" any-state) renders ONLY when the SERVER
    // offered a real choice beyond 'me'. An ownership-fenced agent receives
    // exactly ['me'], so the group (and "Assigned to: Anyone") disappears
    // for clarity -- the server fence, not this hiding, is the control.
    ...(data.list.assigneeOptions.some((o) => o.value !== 'me')
      ? ([
          {
            kind: 'choice',
            key: 'assignee',
            label: 'Assigned to',
            options: data.list.assigneeOptions.map((o) => ({ token: o.value, label: o.label })),
            selected: state.filters.assignee,
            anyLabel: 'Anyone',
          },
        ] as FilterGroupDef[])
      : []),
    ...(data.list.sourceFilterLawful
      ? ([
          {
            kind: 'tokens',
            key: 'source',
            label: 'Source',
            options: LEAD_LIST_SOURCES.map((s) => ({ token: s.token, label: s.label })),
            selected: state.filters.sources,
          },
        ] as FilterGroupDef[])
      : []),
    {
      kind: 'choice',
      key: 'olderThanHours',
      label: 'Lead age',
      options: LEAD_LIST_AGE_PRESETS.map((p) => ({
        token: String(p.hours),
        label: p.label,
      })),
      selected:
        state.filters.olderThanHours !== null ? String(state.filters.olderThanHours) : null,
      anyLabel: 'Any age',
    },
    {
      kind: 'flag',
      key: 'noContact',
      label: 'No contact logged',
      on: state.filters.noContact,
    },
    {
      kind: 'flag',
      key: 'callbackOverdue',
      label: 'Callback overdue',
      on: state.filters.callbackOverdue,
    },
    {
      kind: 'flag',
      key: 'linkPending',
      label: 'Converted - link pending',
      on: state.filters.linkPending,
    },
  ];

  // Recency view: narrow the FENCED page to the records the caller opened,
  // ordered most-recent-first (client-side, over rows the server already
  // fenced -- a foreign id can never surface). Every other view renders the
  // server-ordered page. The page-local search narrows either set.
  const searched = useMemo(() => {
    const base = recencyActive ? orderRecordsByRecency(data.records, recentIds) : data.records;
    return base.filter((r) => leadMatchesQuery(r, query));
  }, [data.records, query, recencyActive, recentIds]);

  const activeFilterCount = chips.length;

  // Filter handlers (moved verbatim from the former inline FilterBar into the
  // right-side panel -- role-shaped groups + server-param semantics unchanged).
  const onToggleToken = (key: string, token: string, selected: boolean): void => {
    if (key === 'stage') {
      const stages = selected
        ? [...state.filters.stages, token]
        : state.filters.stages.filter((s) => s !== token);
      setFilters({ ...state.filters, stages: stages as LeadListFilterState['stages'] });
    } else if (key === 'sla') {
      const slaClasses = selected
        ? [...state.filters.slaClasses, token]
        : state.filters.slaClasses.filter((s) => s !== token);
      setFilters({ ...state.filters, slaClasses: slaClasses as LeadListFilterState['slaClasses'] });
    } else if (key === 'source') {
      const sources = selected
        ? [...state.filters.sources, token]
        : state.filters.sources.filter((s) => s !== token);
      setFilters({ ...state.filters, sources: sources as LeadListFilterState['sources'] });
    }
  };
  const onChoice = (key: string, value: string | null): void => {
    if (key === 'assignee') {
      setFilters({ ...state.filters, assignee: value });
    } else if (key === 'olderThanHours') {
      setFilters({ ...state.filters, olderThanHours: value === null ? null : Number(value) });
    }
  };
  const onFlag = (key: string, on: boolean): void => {
    if (key === 'noContact') setFilters({ ...state.filters, noContact: on });
    else if (key === 'callbackOverdue') setFilters({ ...state.filters, callbackOverdue: on });
    else if (key === 'linkPending') setFilters({ ...state.filters, linkPending: on });
  };
  const onFilterReset = (): void => {
    const def = defaultViewForRole(role);
    onState?.({ view: def.id, filters: def.filters, sort: def.sort, columns: null });
  };

  // M2: the "/" key focuses search (a trivial, isolated, safe-tier
  // enhancement -- NOT a hotkey framework). Ignored while the caret is in any
  // field so it never hijacks typing; cleaned up on unmount.
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el as HTMLElement | null)?.isContentEditable === true
      ) {
        return;
      }
      if (searchRef.current !== null) {
        e.preventDefault();
        searchRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // M7: the needs-attention counts, derived ONLY from the loaded fenced page
  // (searched === the on-screen rows). Every item cites a wire field; a 0 count
  // yields no chip (an omitted number, never a fabricated one). The drill
  // applies an EXISTING lawful filter -- the server ownership fence re-shapes it.
  const needsAttention = useMemo(() => buildNeedsAttention(searched), [searched]);
  const onAttnDrill = (item: NeedsAttentionItem): void => {
    onState?.({
      view: null,
      filters: item.filters,
      sort: state.sort,
      columns: state.columns,
    });
  };

  return (
    <>
      {/* BAND 1 (M1) -- HEADER: who you are + which verified scope, with the
          M6 server-filtering explainer demoted to an accessible (i) hint. */}
      <div
        className="lead-list__band lead-list__band--head"
        data-testid="workspace-context"
      >
        <div className="lead-list__band-head-l">
          <span
            className="leads-workspace__role"
            data-testid="workspace-role"
            data-role={data.role}
          >
            {workspaceRoleLabel(data.role)}
          </span>
          {/* Phase 3 (G1): this surface's own Clinic select is gone -- the
              header lens is the one switcher. The clinic it is showing is
              NAMED here through THE ONE scope primitive. */}
          <ClinicScopeLabel
            label={
              clinicValue === ALL_CLINICS_VALUE
                ? 'All assigned clinics'
                : clinicLabelOf(
                    data.clinics.find(
                      (c) => String(c.clinicId) === clinicValue,
                    )?.clinicLabel ?? null,
                  )
            }
            testId="workspace-clinic-select"
          />
        </div>
        {/* M6: the scope/reveal explainer as an accessible icon-hint. The
            button's aria-label IS the full instruction (AT-reachable); the
            visible glyph + popover are decorative. */}
        <button
          type="button"
          className="lead-list__ihint"
          data-testid="lead-list-scope-hint"
          aria-label={WORKSPACE_SCOPE_HINT}
        >
          <span aria-hidden="true">i</span>
          <span className="lead-list__ihint-pop" aria-hidden="true">
            {WORKSPACE_SCOPE_HINT}
          </span>
        </button>
      </div>

      {/* BAND 2 (M1) -- LIST-VIEW BAR: the Salesforce list-view selector
          (Mission 1) names the active view and carries the copy/bookmark URL
          door (C-11 / C-9), on the neutral inset tone between two white bands. */}
      <div className="lead-list__band lead-list__band--views">
        <LeadViewSelector
          views={views.map((v) => ({ id: v.id, label: v.label, hint: v.hint }))}
          activeViewId={activeViewId}
          onSelect={(id) => {
            const def = views.find((v) => v.id === id);
            if (def !== undefined) {
              onState?.({ view: def.id, filters: def.filters, sort: def.sort, columns: state.columns });
            }
          }}
          onCopyUrl={() => onCopyUrl?.()}
          copied={copied}
        />
      </div>

      {/* Active-filter chips: removable, labeled fields (C-13). */}
      {chips.length > 0 ? (
        <div className="lead-list__chips" data-testid="lead-list-active-chips">
          {chips.map((chip) => (
            <span key={chip.key} className="lead-list__chip">
              {chip.label}
              <button
                type="button"
                aria-label={`Remove filter ${chip.label}`}
                data-testid={`lead-list-chip-remove-${chip.key}`}
                onClick={() => setFilters(removeFilterChip(state.filters, chip.key))}
              >
                {'\u00D7'}
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {/* BAND 3 (M1) -- ACTION TOOLBAR: M2 makes SEARCH the dominant control
          (full-width, leading magnifier, clear-reset, "/" focus hint); the
          Filters button, sort, and columns sit secondary to its right. */}
      <div className="lead-list__band lead-list__band--toolbar">
        <div className="lead-list__search" role="search">
          <svg
            className="lead-list__search-icon"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5 14 14" />
          </svg>
          <input
            ref={searchRef}
            type="search"
            placeholder="Search leads by name, lead #, or clinic"
            aria-label="Search leads on this page"
            data-testid="workspace-search"
            value={query}
            onChange={(e) => onQueryChange?.(e.target.value)}
          />
          {query.length > 0 ? (
            <button
              type="button"
              className="lead-list__search-clear"
              data-testid="workspace-search-clear"
              aria-label="Clear search"
              onClick={() => onQueryChange?.('')}
            >
              {'\u00D7'}
            </button>
          ) : (
            <span className="lead-list__search-kbd" aria-hidden="true">
              /
            </span>
          )}
        </div>
        <div className="lead-list__toolbar-actions">
          {onFindByPhone !== undefined ? (
            <button
              type="button"
              className="lead-list__filters-btn"
              data-testid="lead-list-find-by-phone"
              onClick={() => onFindByPhone()}
            >
              Find by phone
            </button>
          ) : null}
          <button
            type="button"
            className={
              activeFilterCount > 0
                ? 'lead-list__filters-btn lead-list__filters-btn--active'
                : 'lead-list__filters-btn'
            }
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            data-testid="lead-list-filters-button"
            onClick={() => onToggleFilters?.(!filtersOpen)}
          >
            Filters
            {activeFilterCount > 0 ? (
              <span className="lead-list__filters-count" data-testid="lead-list-filters-count">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
          <SortControl
            options={LEAD_LIST_SORTS.map((s) => ({ key: s.key, label: s.label }))}
            active={state.sort}
            onChange={(key) => onState?.({ ...state, sort: key as LeadListSortKey, view: null })}
          />
          <ColumnChooser
            columns={lawfulCols.map((c) => ({ id: c.id, label: c.label }))}
            visible={columns}
            onToggle={(id, on) => {
              const next = on
                ? lawfulCols.map((c) => c.id).filter((cid) => columns.includes(cid) || cid === id)
                : columns.filter((cid) => cid !== id);
              onState?.({ ...state, columns: next });
            }}
            onDefault={() => onState?.({ ...state, columns: null })}
          />
        </div>
      </div>

      {/* M7 -- NEEDS-ATTENTION STRIP: urgency-colored count chips derived ONLY
          from the loaded fenced page; a 0 count renders no chip (never a
          fabricated number). Each chip drills to an EXISTING lawful filter. */}
      {needsAttention.length > 0 ? (
        <div className="lead-list__attn-strip" data-testid="lead-list-attn-strip">
          <span className="lead-list__attn-label">Needs attention</span>
          {needsAttention.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`lead-list__attn lead-list__attn--${item.tone}`}
              data-testid={`lead-list-attn-${item.key}`}
              aria-label={`${item.count} ${item.label} in the current results, filter to these`}
              onClick={() => onAttnDrill(item)}
            >
              <b>{item.count}</b> {item.label}
              <svg
                className="lead-list__attn-chev"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
              >
                <path d="M6 4l4 4-4 4" />
              </svg>
            </button>
          ))}
        </div>
      ) : null}

      {/* M3 -- the enriched COUNT + FRESHNESS line: bold count over the
          ON-SCREEN rows, the active sort, the last successful-fetch freshness,
          and a Refresh affordance (all rendered from real state). */}
      <div className="lead-list__count-rich" data-testid="lead-list-count-line">
        <span className="lead-list__count-n">
          {searched.length} {searched.length === 1 ? 'Lead' : 'Leads'}
        </span>
        <span className="lead-list__count-sep" aria-hidden="true">
          {'\u00B7'}
        </span>
        <span>{countSortLabel(state.sort, recencyActive)}</span>
        {freshnessLabel !== null && freshnessLabel !== undefined ? (
          <>
            <span className="lead-list__count-sep" aria-hidden="true">
              {'\u00B7'}
            </span>
            <span className="lead-list__count-fresh" data-testid="lead-list-freshness">
              {freshnessLabel}
            </span>
          </>
        ) : null}
        {onRefresh !== undefined ? (
          <>
            <span className="lead-list__count-sep" aria-hidden="true">
              {'\u00B7'}
            </span>
            <button
              type="button"
              className="lead-list__refresh"
              data-testid="lead-list-refresh"
              onClick={() => onRefresh()}
              disabled={refreshing}
              aria-label="Refresh the list"
            >
              {refreshing ? (
                <>
                  <span className="lead-list__refresh-spinner" aria-hidden="true" />
                  Refreshing
                </>
              ) : (
                'Refresh'
              )}
            </button>
          </>
        ) : null}
      </div>

      {/* Result truth: the server count honesty (C-8), or -- in the client
          recency view -- the honest "present on this page" scoping. */}
      <p className="lead-list__result-truth" data-testid="lead-list-result-truth">
        {recencyActive
          ? `Recently viewed shows the ${searched.length} lead${
              searched.length === 1 ? '' : 's'
            } you opened from this list that are on this page.`
          : resultTruthLine(data.list)}
        {query.trim().length > 0
          ? ` Search narrows only the ${data.records.length} loaded rows.`
          : ''}
      </p>

      {/* Honest ignored-token notice (C-7/C-9). */}
      {data.list.ignoredFilters.length > 0 ? (
        <p className="lead-list__ignored" data-testid="lead-list-ignored">
          Ignored unknown or unavailable filters: {data.list.ignoredFilters.join(', ')}
        </p>
      ) : null}

      {searched.length > 0 ? (
        <LeadListTable
          records={searched}
          columns={columns}
          role={role}
          clinicValue={clinicValue}
          sort={state.sort}
          onSortKey={(key) => onState?.({ ...state, sort: key, view: null })}
          onQuickView={onQuickView}
          onOpenRecord={onOpenRecord}
          selectedId={selectedId}
        />
      ) : (
        <div className="empty-state" data-testid="workspace-view-empty">
          {recencyActive
            ? recentIds.length === 0
              ? 'You have not opened any leads from this list yet in this browser tab. Open a lead and it appears here, most recent first.'
              : 'The leads you recently opened are not on this page of results. Switch to another view to find them.'
            : data.records.length === 0
              ? chips.length > 0
                ? `No leads match the active filters: ${chips.map((c) => c.label).join(', ')}.`
                : WORKSPACE_EMPTY_COPY
              : 'No leads match this search on the loaded page.'}
        </div>
      )}

      {/* The Salesforce right-side filter panel (Mission 2): conditionally
          mounted, closed by default so the list grammar reads clean. Reuses
          the caller's role-shaped FilterBar groups byte-identically. */}
      {filtersOpen ? (
        <LeadFilterPanel
          groups={filterGroups}
          activeCount={activeFilterCount}
          onToggleToken={onToggleToken}
          onChoice={onChoice}
          onFlag={onFlag}
          onReset={onFilterReset}
          onClose={() => onToggleFilters?.(false)}
        />
      ) : null}
    </>
  );
}

// ---- The self-fetching page (URL-driven; fault-isolated) --------------------

export default function LeadsWorkspacePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const meCtx = useDashboardMeContext();
  const clinicCtx = useClinicLens();

  // Fail-closed role mirror: an unknown/absent role parses as the most
  // restrictive front-desk-tier role. The SERVER remains the boundary.
  const meRole = meCtx?.me?.role ?? null;
  const role: LeadListRole = isLeadListRole(meRole) ? meRole : 'receptionist';

  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1 Phase 3, gap G1).
  // Leads are FENCED (patients are portable, leads are not), so working the
  // lead queue is operational work and this surface reads the lens and only
  // the lens. The URL no longer decides the clinic: it used to SEED from the
  // lens and then diverge the moment either changed, which is the disagreement
  // Phase 2 removed everywhere else. Its own Clinic select is gone with it.
  const clinicValue =
    clinicCtx?.selectedClinicId != null
      ? String(clinicCtx.selectedClinicId)
      : ALL_CLINICS_VALUE;

  // URL -> state: a bare URL lands the role on ITS default view (E-2:
  // zero configuration); any list param makes the URL the exact state.
  const hasListParams = ['v', 'view', 'stage', 'sla', 'assignee', 'source',
    'olderThanHours', 'noContact', 'callbackOverdue', 'linkPending', 'sort',
    'cols'].some((k) => searchParams.get(k) !== null);
  const state: LeadListViewState = useMemo(() => {
    if (hasListParams) return parseLeadListUrl(searchParams, role);
    const def = defaultViewForRole(role);
    return { view: def.id, filters: def.filters, sort: def.sort, columns: null };
  }, [searchParams, role, hasListParams]);

  // Client-recency aware (LEADS-LIST-SALESFORCE-VIEWS-V1): an explicit
  // "Recently viewed" view id wins over the filter-shape match it collides
  // with (see resolveActiveViewId).
  const activeViewId = useMemo(() => resolveActiveViewId(role, state), [role, state]);

  const [data, setData] = useState<LeadsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);
  const [query, setQuery] = useState('');
  // Mission 2: the Salesforce right-side filter panel open state (closed by
  // default -- the list reads clean; the applied tokens stay above the table).
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Mission 1: the "Recently viewed" client memory (per-tab; the fenced page
  // is the only thing it is ever intersected against). Seeded from storage;
  // refreshed whenever the caller opens a lead from this list.
  const [recentIds, setRecentIds] = useState<readonly string[]>(() =>
    readRecentlyViewedIds(),
  );
  // Mission 10: the drawer tracks the row ID and derives the CURRENT record
  // from the latest fetched page, so an in-drawer save (disposition / owner
  // change) refreshes the open drawer in place -- no screen hop, no stale
  // snapshot. A reload that drops the row (fence/filter change) closes it
  // honestly via the null derivation.
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  // R-CD-B1: the search-first enquiry door (renders for the action
  // audience only -- SEE-NOT-DO; the server re-fences every step).
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  // M3: the last SUCCESSFUL-fetch instant (null until the first success -- an
  // error never stamps it, so freshness never falsely reports success) and a
  // manual-refresh-in-flight flag (spinner + disabled Refresh; prevents a
  // duplicate request). `nowTick` re-derives the relative label over time.
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  // CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 10): the
  // config-backed disposition vocabulary for the drawer's panel.
  // Fail-soft: a read fault renders the drawer with no panel.
  const [dispositionOptions, setDispositionOptions] =
    useState<LeadDispositionOptionsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchLeadDispositionOptions()
      .then((value) => {
        if (!cancelled) setDispositionOptions(value);
      })
      .catch(() => {
        if (!cancelled) setDispositionOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const quickViewRecord =
    quickViewId !== null && data !== null
      ? data.records.find((r) => r.safeLeadId === quickViewId) ?? null
      : null;

  const apiParams = useMemo(
    () => apiParamsForState(state, role, activeViewId),
    [state, role, activeViewId],
  );
  const apiParamsKey = JSON.stringify(apiParams);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(undefined);
    fetchLeadsList(
      clinicValue === ALL_CLINICS_VALUE ? null : clinicValue,
      WORKSPACE_LEADS_LIMIT,
      apiParams,
    )
      .then((value) => {
        if (!cancelled) {
          setData(value);
          // M3: stamp freshness ONLY on a real success (never on the error
          // path) so "updated X ago" can never falsely report success.
          setLastLoadedAt(Date.now());
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(safeErrorMessage(e, 'Failed to load leads'));
          setErrorStatus(portalErrorStatus(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicValue, apiParamsKey, reloadKey]);

  // M3: keep the "updated X ago" label honest as time passes (a light 30s
  // tick; the pure label formatter owns the wording). Cleaned up on unmount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  /** M3: manual refresh -- re-runs the EXISTING read (reloadKey) preserving
   *  the current view / filters / search / clinic (all untouched). Guarded so
   *  a click while a load is already in flight cannot fan out a duplicate. */
  const onRefresh = (): void => {
    if (loading || refreshing) return;
    setRefreshing(true);
    setReloadKey((k) => k + 1);
  };

  const freshnessLabel =
    lastLoadedAt !== null ? relativeUpdatedLabel(lastLoadedAt, nowTick) : null;

  /** Write the canonical URL for a state (replace, the clinicId precedent --
   *  Back returns to the previous route; the list state lives in the URL). */
  const writeState = (next: LeadListViewState): void => {
    const params = serializeLeadListUrl(
      {
        ...next,
        // Canonical view id: an explicit client-recency view is preserved;
        // otherwise only an EXACT preset match serializes (resolveActiveViewId).
        view: resolveActiveViewId(role, next),
      },
      role,
    );
    if (clinicValue !== ALL_CLINICS_VALUE) params.set('clinicId', clinicValue);
    setSearchParams(params, { replace: true });
  };

  /** Record that the caller lawfully opened a lead from THIS list (quick view
   *  or the full-record door) into the per-tab "Recently viewed" memory. The
   *  fenced page is the only set it is ever intersected against, so it can
   *  never carry a foreign lead. */
  const noteOpened = (record: Lead360Input): void => {
    setRecentIds(recordLeadOpened(record.safeLeadId, Date.now()));
  };

  const onCopyUrl = (): void => {
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : null;
      if (nav?.clipboard?.writeText !== undefined && typeof window !== 'undefined') {
        void nav.clipboard.writeText(window.location.href);
      }
      setCopied(true);
      if (copyTimer.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(copyTimer.current);
      }
      if (typeof window !== 'undefined') {
        copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
      }
    } catch {
      // Clipboard denied: the URL bar itself remains the bookmarkable truth.
    }
  };

  return (
    <section className="leads-workspace lead-list">
      {/* The page title sits directly above BAND 1 (M1). The M4 prose scope
          banner is retired -- its instruction now rides the M6 (i) icon-hint
          inside the header band, so the view selector + count line lead the
          read (the five-second test). */}
      <div className="lead-list__pagehead">
        <h1>Leads</h1>
      </div>

      {error !== null ? (
        // Error state: a visible failure message + a safe retry that re-runs
        // the EXISTING read and preserves the current view/filter/search state.
        <div className="mb-4" role="alert" data-testid="lead-list-error">
          <DashboardLoadError
            message={error}
            {...(errorStatus !== undefined ? { status: errorStatus } : {})}
          />
          <button
            type="button"
            className="lead-list__retry"
            data-testid="lead-list-retry"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Retry
          </button>
        </div>
      ) : loading && data === null ? (
        // First-load skeleton (M-load): a table-shaped, stable-layout skeleton
        // (no fake data) with a screen-reader announcement; the shimmer is
        // disabled under reduced-motion (CSS).
        <div
          className="lead-list__skeleton"
          data-testid="lead-list-skeleton"
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <span className="lead-list__sr-only">Loading leads</span>
          <div className="lead-list__skeleton-head" aria-hidden="true" />
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="lead-list__skeleton-row" aria-hidden="true" />
          ))}
        </div>
      ) : data !== null && !leadsPayloadRenderable(data) ? (
        // FINDING FIX c (V7-P4-SLICE-2): the malformed-200 payload renders
        // the honest EmptyState instead of throwing on data.list reads.
        <LeadsWorkspaceUnavailable onRetry={() => setReloadKey((k) => k + 1)} />
      ) : data !== null ? (
        <div className={loading ? 'lead-list__stale' : undefined} aria-busy={loading}>
          <LeadsListBody
            data={data}
            role={role}
            state={state}
            clinicValue={clinicValue}
            onState={writeState}
            query={query}
            onQueryChange={setQuery}
            onQuickView={(record) => {
              noteOpened(record);
              setQuickViewId(record.safeLeadId);
            }}
            onOpenRecord={noteOpened}
            onCopyUrl={onCopyUrl}
            copied={copied}
            onFindByPhone={
              canActOnLeadsWorkspace(meCtx?.me?.role)
                ? () => setEnquiryOpen(true)
                : undefined
            }
            selectedId={quickViewId}
            filtersOpen={filtersOpen}
            onToggleFilters={setFiltersOpen}
            recentIds={recentIds}
            freshnessLabel={freshnessLabel}
            onRefresh={onRefresh}
            refreshing={refreshing}
          />
        </div>
      ) : null}

      {enquiryOpen ? (
        <LeadEnquirySearchModal onClose={() => setEnquiryOpen(false)} />
      ) : null}

      {quickViewRecord !== null ? (
        <LeadQuickView
          model={buildQuickViewFromLead360(quickViewRecord, Date.now())}
          me={meCtx?.me ?? null}
          onClose={() => setQuickViewId(null)}
          onChanged={() => {
            // The drawer stays open; the reload re-derives the record.
            setReloadKey((k) => k + 1);
          }}
          /* MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1 (SEE-NOT-DO): the ONE
             disposition panel renders only for the workspace ACTION
             audience. The 2026-07-28 ruling admitted gm_agm / regional_head
             to this list's READ; their save is refused server-side
             (requireLeadsWorkspaceActionScope), so the panel is
             structurally absent for them -- never a dead door (LES-016). */
          dispositionPanel={
            canActOnLeadsWorkspace(meCtx?.me?.role) &&
            dispositionOptions !== null ? (
              <LeadDispositionPanel
                vocabulary={dispositionOptions.dispositionOptions}
                save={(body) =>
                  saveLeadDisposition(String(quickViewRecord.safeLeadId), body)
                }
                onSaved={(): void => setReloadKey((k) => k + 1)}
              />
            ) : undefined
          }
        />
      ) : null}
    </section>
  );
}
